import type {
  ConnectorRouting,
  DrawPoint,
  NodeId,
  Rect,
  SnapGuide,
  Vec,
} from '@kanvas/board-core';

export interface LaserTrail {
  points: Vec[];
  /** Timestamp of the last sample, used to fade the trail out. */
  updatedAt: number;
  color: string;
}

export interface ConnectorPreview {
  from: Vec;
  to: Vec;
  routing: ConnectorRouting;
  /** Node the loose end is hovering, highlighted as a drop target. */
  targetId: NodeId | null;
}

export interface AnchorHint {
  nodeId: NodeId;
  /** Normalised position inside the node box. */
  nx: number;
  ny: number;
  /** Board-space position, precomputed for painting. */
  point: Vec;
}

/**
 * Transient interaction state.
 *
 * Everything here changes at pointer-event rate. Routing it through React (or
 * even through zustand) would mean a component tree update per pointermove, so
 * it lives in one mutable object that the renderer reads directly and that
 * gestures write to, with an explicit invalidate() to schedule a frame.
 */
export interface Scratch {
  /** Rubber-band selection rectangle, board space. */
  marquee: Rect | null;
  /** Points of the stroke currently being drawn, node-local == board space here. */
  drawPoints: DrawPoint[] | null;
  drawColor: string;
  drawSize: number;
  drawBrush: 'pen' | 'marker' | 'highlighter';
  /** Alignment guides produced by the active move or resize. */
  guides: SnapGuide[];
  /** In-flight connector, before it is committed to the document. */
  connectorPreview: ConnectorPreview | null;
  /** Shape being created by a drag, board space. */
  creationPreview: (Rect & { kind: 'shape' | 'frame' | 'sticky' | 'text' | 'notebook' }) | null;
  /** Node under the pointer, for hover affordances. */
  hoverId: NodeId | null;
  /** Connector anchor under the pointer. */
  anchorHint: AnchorHint | null;
  /** Nodes highlighted as connector drop targets or frame drop targets. */
  highlightIds: NodeId[];
  /** Eraser trail, so the user can see what they swept over. */
  eraserPoint: Vec | null;
  eraserRadius: number;
  /** Board-space tip for the draw-tool brush cursor. */
  brushCursorPoint: Vec | null;
  /** Live crop rectangle (0..1 of natural image) while cropping. */
  cropDraft: Rect | null;
  /** Live freehand crop path in board coordinates. */
  cropPath: Vec[] | null;
  /** Draft freehand mask in 0..1 natural-image coordinates. */
  cropMaskDraft: Vec[] | null;
  /** Laser pointer trails keyed by user id, including our own. */
  lasers: Map<string, LaserTrail>;
  /** Cursor to apply to the canvas element. */
  cursor: string;
  /** Set while a gesture owns the pointer. */
  gestureActive: boolean;
}

export const scratch: Scratch = {
  marquee: null,
  drawPoints: null,
  drawColor: '#1A1F28',
  drawSize: 6,
  drawBrush: 'pen',
  guides: [],
  connectorPreview: null,
  creationPreview: null,
  hoverId: null,
  anchorHint: null,
  highlightIds: [],
  eraserPoint: null,
  eraserRadius: 16,
  brushCursorPoint: null,
  cropDraft: null,
  cropPath: null,
  cropMaskDraft: null,
  lasers: new Map(),
  cursor: 'default',
  gestureActive: false,
};

export function resetScratch(): void {
  scratch.marquee = null;
  scratch.drawPoints = null;
  scratch.guides = [];
  scratch.connectorPreview = null;
  scratch.creationPreview = null;
  scratch.anchorHint = null;
  scratch.highlightIds = [];
  scratch.eraserPoint = null;
  scratch.brushCursorPoint = null;
  scratch.cropDraft = null;
  scratch.cropPath = null;
  scratch.cropMaskDraft = null;
  scratch.gestureActive = false;
  scratch.lasers.clear();
}

/**
 * Clears only what a pointer gesture paints while it runs.
 *
 * Escape mid-gesture used `resetScratch`, which also threw away a crop the
 * user had already drawn (while leaving crop mode on) and every peer's laser
 * trail. The gesture's own `cancel` restores its drafts; this just tidies
 * the transient overlays.
 */
export function resetGestureScratch(): void {
  scratch.marquee = null;
  scratch.drawPoints = null;
  scratch.guides = [];
  scratch.connectorPreview = null;
  scratch.creationPreview = null;
  scratch.anchorHint = null;
  scratch.highlightIds = [];
  scratch.cropPath = null;
  scratch.gestureActive = false;
}

type Invalidate = () => void;
let invalidateFn: Invalidate = () => {};

/** The renderer registers its frame scheduler here on mount. */
export function setInvalidator(fn: Invalidate): void {
  invalidateFn = fn;
}

/** Schedule a repaint on the next animation frame. */
export function invalidate(): void {
  invalidateFn();
}

/**
 * Bumped when something the board layer paints changed outside the document,
 * the camera and the editor store — an image finishing its download, say.
 * The renderer caches the painted board between frames and only repaints it
 * when this or one of those inputs moves.
 */
let boardEpoch = 0;

export function boardLayerEpoch(): number {
  return boardEpoch;
}

/** Schedule a repaint that also redraws the cached board layer. */
export function invalidateBoard(): void {
  boardEpoch++;
  invalidateFn();
}

export const LASER_FADE_MS = 900;

export function pruneLasers(now: number): boolean {
  let changed = false;
  for (const [id, trail] of scratch.lasers) {
    if (now - trail.updatedAt > LASER_FADE_MS) {
      scratch.lasers.delete(id);
      changed = true;
    }
  }
  return changed;
}
